// src/scenes/global/Topbar.jsx
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popover,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { colorModeContext } from "../../theme";
import { useNavigate } from "react-router-dom";
import { authGet, getUser, logout } from "../../lib/authApi";

import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";

// Keep this for "Change Password" / account settings
import SettingsDialog from "../../components/SettingsDialog";

const ADMIN_URL = "/admin";

const typeLabel = {
  order: "Order",
  patient: "Patient",
  tox_panel: "Report",
  specimen: "Specimen",
  provider: "Provider",
  facility: "Facility",
};

const statusColor = (s) => {
  const k = String(s || "").toLowerCase();
  if (["final", "completed", "result_imported"].includes(k)) return "success";
  if (["received", "in_process", "review", "pending"].includes(k)) return "info";
  if (["cancelled", "failed", "error"].includes(k)) return "error";
  if (["ordered", "collected", "draft"].includes(k)) return "warning";
  return "default";
};

const Topbar = () => {
  const theme = useTheme();
  const colorMode = useContext(colorModeContext);
  const navigate = useNavigate();
  const searchRef = useRef(null);

  const user = useMemo(() => getUser(), []);
  const role = String(user?.role || localStorage.getItem("role") || "").toLowerCase();
  const isAdmin = ["owner", "global_admin", "global_reader", "superadmin", "admin", "reader"].includes(role);

  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchErr, setSearchErr] = useState("");
  const [groups, setGroups] = useState([]);

  // Profile popover
  const [profileAnchor, setProfileAnchor] = useState(null);
  const profileOpen = Boolean(profileAnchor);

  const handleProfileOpen = (e) => setProfileAnchor(e.currentTarget);
  const handleProfileClose = () => setProfileAnchor(null);

  // Settings dialog
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [settingsStartPage, setSettingsStartPage] = useState("account");

  const openSettingsDialog = (pageKey) => {
    setSettingsStartPage(pageKey);
    setSettingsDialogOpen(true);
  };

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setGroups([]);
      setSearchErr("");
      setSearchBusy(false);
      return;
    }

    const controller = new AbortController();
    const t = setTimeout(async () => {
      setSearchBusy(true);
      setSearchErr("");
      try {
        const orgId = localStorage.getItem("org_id") || user?.org_id || "";
        const resp = await authGet(`/global-search?q=${encodeURIComponent(q)}&org_id=${encodeURIComponent(orgId)}&limit=6`, {
          signal: controller.signal,
        });
        setGroups(resp?.groups || []);
        setSearchOpen(true);
      } catch (e) {
        if (e.name !== "AbortError") setSearchErr(e.message || "Search failed.");
      } finally {
        setSearchBusy(false);
      }
    }, 250);

    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [query, user?.org_id]);

  const handleAdminClick = () => {
    handleProfileClose();
    navigate(ADMIN_URL);
  };

  const goToResult = (result) => {
    setSearchOpen(false);
    setQuery("");
    if (result?.path) navigate(result.path);
  };

  // Backend JWT logout. Do not call Supabase here.
  const handleSignOut = async () => {
    try {
      await logout();
    } finally {
      handleProfileClose();
      navigate("/login", { replace: true });
    }
  };

  const hasResults = groups.some((g) => g.items?.length);

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={1}
        sx={{ flex: "0 0 auto", minHeight: 56, maxHeight: 64, overflow: "visible", gap: 2 }}
      >
        {/* LEFT: Structured global search */}
        <Box sx={{ flex: "1 1 auto", maxWidth: 760 }}>
          <TextField
            inputRef={searchRef}
            size="small"
            fullWidth
            placeholder="Search orders, patients, accessions, reports, tracking, providers, facilities…"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSearchOpen(true);
            }}
            onFocus={() => setSearchOpen(true)}
            onKeyDown={(e) => {
              if (e.key === "Escape") setSearchOpen(false);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {searchBusy ? <CircularProgress size={16} /> : null}
                  {query ? (
                    <IconButton size="small" onClick={() => { setQuery(""); setGroups([]); }} aria-label="Clear search">
                      <ClearOutlinedIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                </InputAdornment>
              ),
            }}
          />

          <Popover
            open={searchOpen && query.trim().length >= 2}
            anchorEl={searchRef.current}
            onClose={() => setSearchOpen(false)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{ sx: { width: searchRef.current?.offsetWidth || 720, maxWidth: "calc(100vw - 32px)", mt: 1 } }}
          >
            <Paper elevation={0} sx={{ maxHeight: "70vh", overflow: "auto" }}>
              {searchErr ? <Alert severity="error" sx={{ m: 1 }}>{searchErr}</Alert> : null}
              {!searchBusy && !hasResults && !searchErr ? (
                <Box p={2}>
                  <Typography variant="body2" color="text.secondary">No structured matches found.</Typography>
                </Box>
              ) : null}
              {groups.map((group) => (
                <Box key={group.type}>
                  <Box px={1.5} pt={1.2} pb={0.5} display="flex" alignItems="center" gap={1}>
                    <Typography variant="caption" color="text.secondary" fontWeight={700} textTransform="uppercase">
                      {group.label}
                    </Typography>
                    <Chip size="small" label={group.items.length} variant="outlined" />
                  </Box>
                  <List dense disablePadding>
                    {group.items.map((result) => (
                      <ListItemButton key={`${result.type}:${result.id}`} onClick={() => goToResult(result)}>
                        <ListItemText
                          primary={
                            <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                              <Typography variant="body2" fontWeight={700} noWrap>{result.title}</Typography>
                              <Chip size="small" label={typeLabel[result.type] || result.type} variant="outlined" />
                              {result.status ? <Chip size="small" color={statusColor(result.status)} label={result.status} /> : null}
                            </Box>
                          }
                          secondary={result.subtitle}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                  <Divider />
                </Box>
              ))}
            </Paper>
          </Popover>
        </Box>

        {/* RIGHT */}
        <Box display="flex" alignItems="center" gap={1}>
          {isAdmin ? (
            <IconButton onClick={handleAdminClick} title="Admin" aria-label="Admin">
              <AdminPanelSettingsOutlinedIcon />
            </IconButton>
          ) : null}

          <IconButton onClick={colorMode.toggleColorMode} title="Toggle theme" aria-label="Toggle theme">
            {theme.palette.mode === "dark" ? <DarkModeOutlinedIcon /> : <LightModeOutlinedIcon />}
          </IconButton>

          <IconButton onClick={handleProfileOpen} title="Profile" aria-label="Profile">
            <PersonOutlineOutlinedIcon />
          </IconButton>

          <Popover
            open={profileOpen}
            anchorEl={profileAnchor}
            onClose={handleProfileClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuList>
              {isAdmin ? (
                <>
                  <MenuItem onClick={handleAdminClick}>Admin</MenuItem>
                  <Divider />
                </>
              ) : null}

              <MenuItem
                onClick={() => {
                  handleProfileClose();
                  openSettingsDialog("account");
                }}
              >
                Change Password
              </MenuItem>

              <Divider />

              <MenuItem onClick={handleSignOut}>Log Out</MenuItem>
            </MenuList>
          </Popover>
        </Box>
      </Box>

      <SettingsDialog
        open={settingsDialogOpen}
        onClose={() => setSettingsDialogOpen(false)}
        startPage={settingsStartPage}
      />
    </>
  );
};

export default Topbar;
